/**
 * PRP-WEB-2: what the editor draws over the plan, in plan feet. Hover highlight on whatever the
 * pointer would grab; the selected wall's arrow handle and the selected door's slide handle;
 * snap guides and the live dimension label while dragging; the drag-out room's outline; the
 * split tool's line; subroom numbers (W2.5); red outlines on overlapping rooms (W2.6); the
 * underlay's calibrate line (W2.7). Never takes the pointer.
 */
import {
  bbox, dist, edgeOf, findRoom, labelPt, outN, type Plan, type Pt,
} from '@sketch-core';
import type { DragFrame, Sel } from '../controller/editController';
import { handlePt } from '../controller/editController';
import type { Groups } from '../groups/groups';

export type HoverTarget = Sel | null;

export interface EditorOverlayProps {
  plan: Plan;
  sel: Sel | null;
  hover: HoverTarget;
  frame: DragFrame | null;
  groups: Groups;
  /** Rooms the Problems strip marks red. */
  problemRooms: Set<string>;
  /** The split tool's preview line. */
  splitLine: [Pt, Pt] | null;
  /** The underlay calibrate line being drawn. */
  calibrate: [Pt, Pt] | null;
  /** Plan feet per screen pixel (handle and label sizes). */
  px: number;
}

const ACCENT = 'var(--accent)';

function wallSeg(plan: Plan, rid: string, vid: number): [Pt, Pt] | null {
  const r = findRoom(plan, rid);
  const E = r && edgeOf(r, vid);
  return E ? [E.a, E.b] : null;
}

function openingSeg(plan: Plan, rid: string, oid: number): [Pt, Pt] | null {
  const r = findRoom(plan, rid);
  const o = r?.openings.find((q) => q.id === oid);
  const E = r && o ? edgeOf(r, o.edgeStart) : null;
  if (!E || !o) return null;
  const L = dist(E.a, E.b) || 1;
  const at = (s: number) => ({ x: E.a.x + ((E.b.x - E.a.x) * s) / L, y: E.a.y + ((E.b.y - E.a.y) * s) / L });
  return [at(o.offFt), at(o.offFt + o.widthFt)];
}

function Highlight({ plan, t, px, strong }: { plan: Plan; t: Sel; px: number; strong: boolean }) {
  const w = (strong ? 5 : 4) * px;
  const op = strong ? 0.9 : 0.45;
  if (t.type === 'room') {
    const r = findRoom(plan, t.rid);
    if (!r) return null;
    return strong ? null : (
      <polygon data-testid="plan-hover-room" points={r.corners.map((p) => `${p.x},${p.y}`).join(' ')}
        fill={ACCENT} fillOpacity={0.06} stroke={ACCENT} strokeOpacity={0.5} strokeWidth={2 * px} />
    );
  }
  const seg = t.type === 'wall' ? wallSeg(plan, t.rid, t.vid) : openingSeg(plan, t.rid, t.oid);
  if (!seg) return null;
  return (
    <line data-testid={strong ? `plan-sel-${t.type}` : `plan-hover-${t.type}`} x1={seg[0].x} y1={seg[0].y} x2={seg[1].x} y2={seg[1].y}
      stroke={ACCENT} strokeOpacity={op} strokeWidth={w} strokeLinecap="round" />
  );
}

/** The selected wall's two-way arrow (drag across the wall) or the door's slide grip. */
function Handle({ plan, sel, px }: { plan: Plan; sel: Sel; px: number }) {
  const c = handlePt(plan, sel);
  const r = findRoom(plan, sel.rid);
  if (!c || !r) return null;
  const vid = sel.type === 'wall' ? sel.vid : sel.type === 'open' ? r.openings.find((o) => o.id === sel.oid)?.edgeStart : undefined;
  const E = vid != null ? edgeOf(r, vid) : null;
  if (!E) return null;
  const n = outN(r, E.a, E.b);
  const u = { x: (E.b.x - E.a.x) / (dist(E.a, E.b) || 1), y: (E.b.y - E.a.y) / (dist(E.a, E.b) || 1) };
  const d = sel.type === 'wall' ? n : u; // a wall moves across itself, a door along its wall
  const L = 14 * px, h = 5 * px;
  const tip = (s: number) => ({ x: c.x + d.x * L * s, y: c.y + d.y * L * s });
  const head = (s: number) => {
    const t = tip(s), b = { x: t.x - d.x * h * s, y: t.y - d.y * h * s }, q = { x: -d.y * h * 0.8, y: d.x * h * 0.8 };
    return `${t.x},${t.y} ${b.x + q.x},${b.y + q.y} ${b.x - q.x},${b.y - q.y}`;
  };
  return (
    <g data-testid={`plan-handle-${sel.type}`}>
      <circle cx={c.x} cy={c.y} r={7 * px} fill="var(--bg-2)" stroke={ACCENT} strokeWidth={2 * px} />
      <line x1={tip(-1).x} y1={tip(-1).y} x2={tip(1).x} y2={tip(1).y} stroke={ACCENT} strokeWidth={2 * px} />
      <polygon points={head(1)} fill={ACCENT} />
      <polygon points={head(-1)} fill={ACCENT} />
    </g>
  );
}

function Label({ at, text, px, testId }: { at: Pt; text: string; px: number; testId: string }) {
  const fs = 12 * px, w = text.length * fs * 0.58 + 12 * px, h = fs * 1.7;
  return (
    <g data-testid={testId}>
      <rect x={at.x - w / 2} y={at.y - h / 2} width={w} height={h} rx={h / 2} fill="var(--tx-hi)" fillOpacity={0.88} />
      <text x={at.x} y={at.y} fontSize={fs} fontWeight={650} textAnchor="middle" dominantBaseline="central" fill="var(--bg-1)">{text}</text>
    </g>
  );
}

export function EditorOverlay({ plan, sel, hover, frame, groups, problemRooms, splitLine, calibrate, px }: EditorOverlayProps) {
  const same = (a: Sel | null, b: Sel | null) => JSON.stringify(a) === JSON.stringify(b);
  return (
    <g pointerEvents="none" data-testid="plan-editor-overlay">
      {[...problemRooms].map((rid) => {
        const r = findRoom(plan, rid);
        return r ? (
          <polygon key={`p${rid}`} data-testid="plan-problem-room" points={r.corners.map((p) => `${p.x},${p.y}`).join(' ')}
            fill="var(--bad)" fillOpacity={0.1} stroke="var(--bad)" strokeWidth={2 * px} strokeDasharray={`${6 * px} ${4 * px}`} />
        ) : null;
      })}
      {groups.flatMap((g) => g.subroom_ids.map((rid, i) => {
        const r = findRoom(plan, rid);
        if (!r) return null;
        const [x0, y0] = bbox(r.corners);
        const c = labelPt(r);
        const at = { x: Math.max(x0 + 9 * px, c.x - 28 * px), y: Math.max(y0 + 9 * px, c.y - 22 * px) };
        return (
          <g key={`n${rid}`} data-testid="plan-subroom-number" data-room={rid} data-number={i + 1}>
            <circle cx={at.x} cy={at.y} r={8 * px} fill={ACCENT} />
            <text x={at.x} y={at.y} fontSize={10 * px} fontWeight={700} textAnchor="middle" dominantBaseline="central" fill="var(--accent-tx)">{i + 1}</text>
          </g>
        );
      }))}
      {hover && !frame && !same(hover, sel) && <Highlight plan={plan} t={hover} px={px} strong={false} />}
      {sel && <Highlight plan={plan} t={sel} px={px} strong />}
      {sel && !frame && sel.type !== 'room' && <Handle plan={plan} sel={sel} px={px} />}
      {frame?.guides.map(([a, b], i) => (
        <line key={`g${i}`} data-testid="plan-snap-guide" x1={a.x} y1={a.y} x2={b.x} y2={b.y} stroke={ACCENT} strokeWidth={1.5 * px} strokeDasharray={`${5 * px} ${4 * px}`} />
      ))}
      {frame?.snapLine && (
        <line data-testid="plan-snap-line" x1={frame.snapLine[0].x} y1={frame.snapLine[0].y} x2={frame.snapLine[1].x} y2={frame.snapLine[1].y}
          stroke={ACCENT} strokeWidth={1.5 * px} strokeDasharray={`${5 * px} ${4 * px}`} />
      )}
      {frame?.outline && (
        <polygon data-testid="plan-dragout" points={frame.outline.map((p) => `${p.x},${p.y}`).join(' ')}
          fill={ACCENT} fillOpacity={0.12} stroke={ACCENT} strokeWidth={2 * px} strokeDasharray={`${6 * px} ${4 * px}`} />
      )}
      {splitLine && (
        <line data-testid="plan-split-preview" x1={splitLine[0].x} y1={splitLine[0].y} x2={splitLine[1].x} y2={splitLine[1].y}
          stroke={ACCENT} strokeWidth={2 * px} strokeDasharray={`${6 * px} ${4 * px}`} />
      )}
      {calibrate && (
        <g data-testid="plan-calibrate-line">
          <line x1={calibrate[0].x} y1={calibrate[0].y} x2={calibrate[1].x} y2={calibrate[1].y} stroke="var(--bad)" strokeWidth={2.5 * px} />
          {calibrate.map((p, i) => <circle key={i} cx={p.x} cy={p.y} r={4 * px} fill="var(--bad)" />)}
        </g>
      )}
      {frame?.label && <Label at={frame.label.at} text={frame.label.text} px={px} testId="plan-live-label" />}
    </g>
  );
}

export default EditorOverlay;
